/**
 * Early Payoff Guide Modal (P20251023-UI-P5E)
 *
 * Displays guided steps for early payoff when Stripe Portal doesn't support direct deep-link.
 * PII-free, customer-safe instructions.
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, ExternalLink } from 'lucide-react';

interface EarlyPayoffGuideProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenPortal: () => void;
}

export default function EarlyPayoffGuide({
  open,
  onOpenChange,
  onOpenPortal,
}: EarlyPayoffGuideProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Early Payoff - How It Works</DialogTitle>
          <DialogDescription>
            Settle your remaining balance early and own your domain immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Early payoff allows you to pay the remaining balance on your lease-to-own plan and
              immediately own the domain outright.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Steps to Pay Off Early:</h4>
            <ol className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="flex-shrink-0 font-semibold">1.</span>
                <span>
                  Open your Billing Portal using the button below to view your current
                  subscription and payment history.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 font-semibold">2.</span>
                <span>
                  Contact me with your subscription ID and request early payoff.
                  I'll calculate your remaining balance.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 font-semibold">3.</span>
                <span>
                  I'll send you a secure payment link for the remaining balance (minus any
                  applicable discounts).
                </span>
              </li>
              <li className="flex gap-2">
                <span className="flex-shrink-0 font-semibold">4.</span>
                <span>
                  Once payment is received, I'll immediately initiate the full domain transfer to
                  your registrar.
                </span>
              </li>
            </ol>
          </div>

          <Alert>
            <AlertDescription className="text-xs">
              <strong>Note:</strong> Early payoff typically includes a small discount on remaining
              payments. Contact support for your specific quote.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={onOpenPortal}>
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Billing Portal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

